import { motion } from 'framer-motion';
import { FadeInSection } from '@/components/ui/FadeInSection';
import { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface Step {
  number: string;
  title: string;
  description: string;
  icon?: LucideIcon;
}

interface MethodologyTimelineProps {
  title?: string;
  subtitle?: string;
  steps: Step[];
  size?: 'default' | 'compact';
  accentColor?: 'green' | 'blue';
}

/**
 * Timeline verticale moderne pour afficher une méthodologie en étapes
 */
export function MethodologyTimeline({
  title,
  subtitle,
  steps,
  size = 'default',
  accentColor = 'green',
}: MethodologyTimelineProps) {
  const { t } = useTranslation('prestations');

  const displayTitle = title || t('methodology.defaultTitle');
  const displaySubtitle = subtitle || t('methodology.defaultSubtitle');
  const isCompact = size === 'compact';

  const colorConfig = {
    green: {
      circleBg: 'bg-[#0D974E]',
      line: 'from-aspexit-green via-aspexit-green to-aspexit-green',
      iconBg: 'bg-aspexit-green/10',
      iconColor: 'text-aspexit-green',
    },
    blue: {
      circleBg: 'bg-gradient-to-br from-blue-500 to-blue-700',
      line: 'from-blue-500 via-blue-600 to-blue-500',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
    },
  };

  const colors = colorConfig[accentColor];

  return (
    <section className={isCompact ? 'py-16 bg-gradient-to-b from-white to-aspexit-gray-light' : 'py-24 bg-gradient-to-b from-white to-aspexit-gray-light'}>
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <FadeInSection>
            <div className={isCompact ? 'text-center mb-12' : 'text-center mb-16'}>
              <h2 className={isCompact ? 'text-3xl md:text-4xl font-heading font-extrabold text-aspexit-black mb-3' : 'text-4xl md:text-5xl font-heading font-extrabold text-aspexit-black mb-4'}>
                {displayTitle}
              </h2>
              <p className={isCompact ? 'text-lg text-aspexit-gray' : 'text-xl text-aspexit-gray'}>
                {displaySubtitle}
              </p>
            </div>
          </FadeInSection>

          {/* Timeline */}
          <div className="relative">
            {/* Ligne verticale */}
            <div className={`absolute left-6 md:left-8 top-6 bottom-6 w-0.5 bg-gradient-to-b ${colors.line} opacity-30`} />

            {/* Étapes */}
            <div className={isCompact ? 'space-y-6' : 'space-y-12'}>
              {steps.map((step, index) => {
                const Icon = step.icon;
                return (
                  <FadeInSection key={index} delay={index * 0.1}>
                    <motion.div
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className={isCompact ? 'relative flex gap-4 md:gap-5' : 'relative flex gap-6 md:gap-8'}
                    >
                      {/* Cercle numéro */}
                      <div className="flex-shrink-0">
                        <div className={`${isCompact ? 'w-12 h-12 md:w-16 md:h-16' : 'w-16 h-16 md:w-24 md:h-24'} rounded-full ${colors.circleBg} flex items-center justify-center shadow-lg relative z-10`}>
                          <span className={`text-white font-heading font-extrabold ${isCompact ? 'text-lg md:text-xl' : 'text-2xl md:text-3xl'}`}>
                            {step.number}
                          </span>
                        </div>
                      </div>

                      {/* Contenu */}
                      <div className={`flex-grow bg-white rounded-xl ${isCompact ? 'p-4 md:p-5' : 'p-6 md:p-8'} shadow-md hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-gray-100`}>
                        <div className={isCompact ? 'flex items-start gap-3 mb-2' : 'flex items-start gap-4 mb-3'}>
                          {Icon && (
                            <div className={`${isCompact ? 'w-8 h-8' : 'w-10 h-10'} rounded-lg ${colors.iconBg} flex items-center justify-center flex-shrink-0`}>
                              <Icon className={`${isCompact ? 'w-4 h-4' : 'w-5 h-5'} ${colors.iconColor}`} />
                            </div>
                          )}
                          <h3 className={`${isCompact ? 'text-lg md:text-xl' : 'text-2xl'} font-heading font-bold text-aspexit-black`}>
                            {step.title}
                          </h3>
                        </div>
                        <p className={`${isCompact ? 'text-sm' : 'text-base'} text-aspexit-gray leading-relaxed`}>
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  </FadeInSection>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
